import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { UserNotificationPreferenceViewStore } from "../../store";
import { UserNotificationPreferenceView } from "../view/UserNotificationPreferenceView";
import { UserNotificationPreferenceUpdateStore } from "../../store";
import { UserNotificationPreferenceUpdate } from "../update/UserNotificationPreferenceUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type UserNotificationPreferenceModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const UserNotificationPreferenceModalContent: React.FC<UserNotificationPreferenceModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new UserNotificationPreferenceUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new UserNotificationPreferenceViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <UserNotificationPreferenceView
            userNotificationPreferenceViewStore={
              storeInfo.store as UserNotificationPreferenceViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <UserNotificationPreferenceUpdate
            userNotificationPreferenceUpdateStore={
              storeInfo.store as UserNotificationPreferenceUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
